import React, { useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useStyles } from './eventsAndChallengesStyles';
import { AddEvent } from './AddEvent';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './eventsAndChallenges.css';
import { getEvents, deleteEvent, updateEvent } from '../../API/events';
import EventStudentsModal from './EventStudentsModal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const EventsManagement: React.FC = () => {
    const [events, setEvents] = React.useState<any[]>([]);
    const [eventToEdit, setEventToEdit] = React.useState<any>(undefined);
    const [rows, setRows] = React.useState<any[]>([]);
    const [modalShow, setModalShow] = React.useState<boolean>(false);
    const [changeIsActive, setChangeIsActive] = React.useState<boolean>(false);
    const [studentsModalOpen, setStudentsModalOpen] = React.useState<boolean>(false);
    const [selectedEvent, setSelectedEvent] = React.useState<any>(null);
    const user = useSelector((state: any) => state.auth);

    // Base columns visible to all users
    const baseColumns = [
        "Event Name",
        "Age Group",
        "Event Type",
        "Start Date",
        "End Date",
        "Mode",
        "Status",
        "Action",
        "Edit",
        "View Students",
    ];

    // Delete column visible only to superusers
    const deleteColumn = ["Delete"];

    // Combine columns based on user.isSuper
    const columns = user.isSuper || user.isContentAdmin ? [...baseColumns, ...deleteColumn] : baseColumns;

    const getEvent = React.useCallback(async () => {
        const newEvents = await getEvents();
        setEvents(newEvents);
    }, []);

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                await deleteEvent(id);
                setEvents(events.filter((event: any) => event._id !== id));
            } catch (error) {
                console.error('Error deleting event:', error);
            }
        }
    };

    const handleViewStudents = (event: any) => {
        setSelectedEvent(event);
        setStudentsModalOpen(true);
    };

    useEffect(() => {
        getEvent();
    }, [modalShow, getEvent]);

    useEffect(() => {
        if (events) {
            const newEvents = events.map((event: any) => {
                let status = "Inactive";
                if (event.isCompleted) status = "Completed";
                else if (event.isActive) status = "Active";

                return {
                    _id: event._id as string,
                    title: event.title as string,
                    ageGroup: `${event.minAge} Yrs-${event.maxAge} Yrs`,
                    eventType: "Activity",
                    startDate: event.startDate ? event.startDate as string : "2021-01-01",
                    endDate: event.endDate ? event.endDate as string : "2021-01-01",
                    mode: event.mode as string,
                    status: status,
                    isCompleted: event.isCompleted || false,
                    isActive: event.isActive || false,
                    action: event.isActive ? "Deactivate" : "Activate",
                };
            });
            setRows(newEvents);
        }
    }, [events]);

    const classes = useStyles();

    const handleUpdate = (id: string, toChangeIsActive: boolean) => {
        setChangeIsActive(toChangeIsActive);
        setEventToEdit(events.find((event: any) => event._id === id));
        setModalShow(true);
    };

    const handleClose = () => {
        setModalShow(false);
        setEventToEdit(undefined);
        setChangeIsActive(false);
    };

    const handleMarkAsCompleted = async (row: any) => {
        if (window.confirm('Are you sure you want to mark this workshop as completed?')) {
            try {
                const originalEvent = events.find((event: any) => event._id === row._id);
                if (!originalEvent) {
                    toast.error("Workshop not found");
                    return;
                }
                const updatedEvent = { ...originalEvent, isCompleted: true };
                const eventString = JSON.stringify(updatedEvent);
                await updateEvent(eventString);
                toast.success("Workshop marked as completed");
                setEvents(events.map((event: any) =>
                    event._id === row._id ? { ...event, isCompleted: true } : event
                ));
            } catch (error) {
                console.error('Error marking workshop as completed:', error);
                toast.error("Failed to mark workshop as completed");
            }
        }
    };

    const getStatusClassName = (status: string) => {
        if (status === "Completed") return "completedStatus";
        return status === "Active" ? "activeMe" : "inActive";
    };

    return (
        <div>
            <div className="tableHeader active-courses">
                <button className="addChallengeBtn" onClick={() => setModalShow(true)}>
                    <div className="btnContainer">
                        <span>Add Workshop</span>
                        <span>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M11.25 5.25H6.75V0.75C6.75 0.551088 6.67098 0.360322 6.53033 0.21967C6.38968 0.0790178 6.19891 0 6 0C5.80109 0 5.61032 0.0790178 5.46967 0.21967C5.32902 0.360322 5.25 0.551088 5.25 0.75V5.25H0.75C0.551088 5.25 0.360322 5.32902 0.21967 5.46967C0.0790178 5.61032 0 5.80109 0 6C0 6.19891 0.0790178 6.38968 0.21967 6.53033C0.360322 6.67098 0.551088 6.75 0.75 6.75H5.25V11.25C5.25 11.4489 5.32902 11.6397 5.46967 11.7803C5.61032 11.921 5.80109 12 6 12C6.19891 12 6.38968 11.921 6.53033 11.7803C6.67098 11.6397 6.75 11.4489 6.75 11.25V6.75H11.25C11.4489 6.75 11.6397 6.67098 11.7803 6.53033C11.921 6.38968 12 6.19891 12 6C12 5.80109 11.921 5.61032 11.7803 5.46967C11.6397 5.32902 11.4489 5.25 11.25 5.25Z"
                                    fill="#EB1556"
                                />
                            </svg>
                        </span>
                    </div>
                </button>
                {modalShow && <AddEvent data={eventToEdit} handleClose={handleClose} changeIsActive={changeIsActive} />}
            </div>
            <div>
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col}
                                        align="center"
                                        className={classes.colTitleCell}
                                        sx={{
                                            fontSize: '15px',
                                            padding: '8px',
                                            minWidth: col === "Action" ? '250px' : '100px',
                                        }}
                                    >
                                        <span className={classes.colTitle}>{col}</span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row: any) => (
                                <TableRow key={row._id}>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>{row.title}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>
                                        <div className="divBtn">
                                            <span className="divBtnText">{row.ageGroup}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>{row.eventType}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>{row.startDate}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>{row.endDate}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>{row.mode}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>
                                        <div className={`divBtn ${getStatusClassName(row.status)}`}>
                                            {row.status}
                                        </div>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px', minWidth: '250px' }}>
                                        {row.isCompleted ? (
                                            <div className="success-actions">
                                                <div className="success-icon">
                                                    <CheckCircleOutlineIcon sx={{ color: "green", fontSize: 24 }} />
                                                </div>
                                                <div className="success-message">Ready for attendance</div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                                <button
                                                    className={`divBtn ${row.action === "Activate" ? "activate" : "deactivate"}`}
                                                    onClick={() => handleUpdate(row._id, true)}
                                                >
                                                    {row.action === "Activate" ?
                                                        <UploadIcon sx={{ fontSize: 20, color: "grey" }} /> :
                                                        <DownloadIcon sx={{ fontSize: 20, color: "grey" }} />
                                                    }
                                                    {row.action}
                                                </button>
                                                <button
                                                    className="divBtn markCompleted"
                                                    onClick={() => handleMarkAsCompleted(row)}
                                                >
                                                    <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "green" }} />
                                                    Complete
                                                </button>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>
                                        <button
                                            onClick={() => handleUpdate(row._id, false)}
                                            className="btn btn-light repair"
                                        >
                                            <ModeEditIcon sx={{ fontSize: 25, color: "grey" }} />
                                        </button>
                                    </TableCell>
                                    {/* Conditionally render Delete column */}
                                    <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>
                                        <button
                                            onClick={() => handleViewStudents(row)}
                                            className="btn btn-light view"
                                        >
                                            <VisibilityIcon sx={{ fontSize: 25, color: "grey" }} />
                                        </button>
                                    </TableCell>
                                    {(user.isSuper || user.isContentAdmin) && (
                                        <TableCell align="center" sx={{ fontSize: '15px', padding: '8px' }}>
                                            <button
                                                onClick={() => handleDelete(row._id)}
                                                className="btn btn-light delete"
                                            >
                                                <DeleteIcon sx={{ fontSize: 25, color: "grey" }} />
                                            </button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            {selectedEvent && (
                <EventStudentsModal
                    eventId={selectedEvent._id}
                    eventTitle={selectedEvent.title}
                    eventEndDate={selectedEvent.endDate}
                    open={studentsModalOpen}
                    onClose={() => setStudentsModalOpen(false)}
                    isCompleted={selectedEvent.isCompleted}
                />
            )}
        </div>
    );
};

export default EventsManagement;